import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  OutlinedInput,
  Slide,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { KeyboardEvent } from "react";
import { debounce } from "throttle-debounce";
import Modal, { CommonModalProps } from "..";
import { ExternalAccessDto } from "../../../Core/Api/Types/access-models";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { accessService, assetService, searchService, userService } from "../../../Core/Api/services";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import AccessCustomChip from "../../Access/Shared/access-custom-chip";
import AccessCustomChipsSummary from "../../Access/Shared/access-custom-chips-summary";
import AssetAndChildrenAccordion from "../../Access/Shared/asset-and-children-accordion";
import AssetAndChildrenCard from "../../Access/Shared/asset-and-children-card";
import PermissionsFormSection from "../../Access/Shared/permissions-form-section";
import FormInputDate from "../../Forms/FormInputDate/form-input-date";
import FormInputEmail from "../../Forms/FormInputEmail/form-input-email";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import FormSection from "../../Forms/FormSection/form-section";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

export type ModalCreateOrUpdateExternalAccessElementProps = {
  assets?: DbAssetObjectInfos[];
  accessToUpdate?: ExternalAccessDto;
  onSuccess?: () => void;
};

export type ModalCreateOrUpdateExternalAccessProps = CommonModalProps<ModalCreateOrUpdateExternalAccessElementProps>;

const ModalCreateOrUpdateExternalAccess = React.forwardRef<any, ModalCreateOrUpdateExternalAccessProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { assets, accessToUpdate, onSuccess, closeModal } = props;

    const formId = "form-create-external-access";
    const isFormValid = useAppSelector(GetFormIsValid(formId));

    const enqueueSnackbar = useCustomSnackbar();

    // #region States
    // Step 1 - Asset(s) not selected
    const [selectedAssets, setSelectedAssets] = React.useState<DbAssetObjectInfos[]>(assets ?? []);
    const [creationStep, setCreationStep] = React.useState<"no-asset" | "asset-selected">(
      !assets || assets.length === 0 ? "no-asset" : "asset-selected"
    );
    const [direction, setDirection] = React.useState<"left" | "right">("left");
    // Control transition display
    const [showTransition, setShowTransition] = React.useState<boolean>(false);
    const [canGoBack, setCanGoBack] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [searchLoading, setSearchLoading] = React.useState<boolean>(false);
    const [searchResults, setSearchResults] = React.useState<DbAssetObjectInfos[]>([]);

    // Step 2 - Asset(s) selected
    const { active: emailsExpanded, toggle: toggleEmailsExpanded } = useToggle(!accessToUpdate);
    const { active: optionsExpanded, toggle: toggleOptionsExpanded } = useToggle(true);

    const [fetchedMailingList, setFetchedMailingList] = React.useState<MailingListDto[]>([]);
    const [showCustomEmail, setShowCustomEmail] = React.useState<boolean>(false);

    const [virtualHeadName, setVirtualHeadName] = React.useState<string>("");
    const [selectedPermissions, setSelectedPermissions] = React.useState<PermissionType[]>(
      accessToUpdate ? accessToUpdate.permissions : GlobalConst.EXTERNAL_ACCESS_MANDATORY_PERMISSIONS
    );
    const {
      selected: selectedEmails,
      select: selectEmail,
      selectList: selectEmailList,
    } = useMultipleSelection<string>(accessToUpdate ? [accessToUpdate.email] : []);
    const [manualEmailValue, setManualEmailValue] = React.useState<string>("");
    const [expirationDate, setExpirationDate] = React.useState<Date | null>(
      accessToUpdate ? new Date(accessToUpdate.expirationDate) : null
    );
    const [emailObject, setEmailObject] = React.useState<string | null>(null);
    const [emailContent, setEmailContent] = React.useState<string | null>(null);

    const [creationLoading, setCreationLoading] = React.useState<boolean>(false);
    // #endregion States

    // #region Funcs
    // Step 1 - Asset(s) not selected
    const handleCancelClick = () => {
      closeModal();
    };

    // search assets
    const searchAssets = React.useCallback((searchValue: string) => {
      if (!searchValue || searchValue.isEmpty()) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);

      // Search on Asset and Group layers
      const searchPromises = [
        searchService.SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Group,
            hideTitleWithoutAsset: true,
            classifications: [ObjectClass.Drive],
            notInAssetIds: selectedAssetIds,
          })
        ),
        searchService.SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Asset,
            hideTitleWithoutAsset: true,
            classifications: [ObjectClass.Drive],
            notInAssetIds: selectedAssetIds,
          })
        ),
        searchService.SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Clip,
            hideTitleWithoutAsset: true,
            classifications: [ObjectClass.Drive],
            notInAssetIds: selectedAssetIds,
          })
        ),
      ];

      Promise.all(searchPromises)
        .then((responses) => {
          const groupResponse = responses[0];
          const assetResponse = responses[1];
          const clipResponse = responses[2];

          if (!groupResponse.success || !assetResponse.success || !clipResponse.success) {
            if (!groupResponse.success)
              enqueueSnackbar(t("alert:error.searchingForFolders", { message: groupResponse.message }), "error");
            if (!assetResponse.success)
              enqueueSnackbar(t("alert:error.searchingForAssets", { message: groupResponse.message }), "error");
            if (!clipResponse.success)
              enqueueSnackbar(t("alert:error.searchingForClips", { message: groupResponse.message }), "error");

            setSearchResults([]);
            setSearchLoading(false);
            return;
          }
          const searchResults = (groupResponse.results ?? [])
            .concat(assetResponse.results ?? [])
            .concat(clipResponse.results ?? []);

          // Set search results
          setSearchResults(searchResults);
        })
        .finally(() => setSearchLoading(false));
    }, []);

    // throttle
    const debounceSearchTitles = React.useMemo(
      () =>
        debounce(GlobalConst.SEARCH_THROTTLE_DELAY_MS, searchAssets, {
          //noLeading: true,
          atBegin: false,
        }),
      [searchAssets]
    );

    const handleSearchInputChange = (searchValue: string) => {
      setSearchValue(searchValue);
      debounceSearchTitles(searchValue);
    };

    const handleOnSelectAsset = (asset: DbAssetObjectInfos) => {
      const indexOfAsset = selectedAssets.findIndex((a) => a.id === asset.id);
      if (indexOfAsset === -1) setSelectedAssets((prevState) => [...prevState, asset]);
      else setSelectedAssets((prevState) => prevState.filter((a) => a.id !== asset.id));
    };

    const handleNextStep = () => {
      setCreationStep("asset-selected");
      setDirection("left");
      setShowTransition(true);
    };

    const handlePreviousStep = () => {
      setCreationStep("no-asset");
      setDirection("right");
      setShowTransition(true);
    };

    // Step 2 - Asset(s) selected
    const handlePermissionClick = (permission: PermissionType) => {
      if (selectedPermissions.includes(permission)) {
        setSelectedPermissions(selectedPermissions.filter((p) => p !== permission));
      } else {
        setSelectedPermissions([...selectedPermissions, permission]);
      }
    };

    const handlePressEnterMailingManual = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") return;

      if (!selectedEmails.includes(manualEmailValue)) selectEmail(manualEmailValue);

      setManualEmailValue("");
    };

    const handleAutocompleteMailingListChange = (value: string | null) => {
      if (value === null || value === "") return;

      const mailingListEmails = fetchedMailingList.find((l) => l.name === value)?.emails || [];
      selectEmailList(
        mailingListEmails.filter((email) => !selectedEmails.includes(email)),
        false
      );
    };

    const handleCreateClick = React.useCallback(() => {
      setCreationLoading(true);

      // Create external access
      accessService
        .CreateExternalAccess({
          virtualHeadName: virtualHeadName,
          objectIds: selectedAssets.map((asset) => asset.id),
          permissions: selectedPermissions,
          emails: selectedEmails,
          expirationDate: expirationDate!,
          emailSubject: emailObject || null,
          emailMessage: emailContent,
        })
        .then((response) => {
          if (!response.success) {
            enqueueSnackbar(
              t("alert:error.creatingExternalAccess", { count: selectedEmails.length, message: response.message }),
              "error"
            );
            return;
          } else {
            enqueueSnackbar(t("alert:success.externalAccessCreated", { count: selectedEmails.length }), "success");
            onSuccess && onSuccess();
            closeModal();
          }
        })
        .finally(() => {
          setCreationLoading(false);
        });
    }, [selectedAssets, selectedPermissions, selectedEmails, expirationDate, emailObject, emailContent]);

    const handleUpdateClick = React.useCallback(() => {
      accessService
        .UpdateExternalAccess({
          id: accessToUpdate!.id,
          permissions: selectedPermissions,
          expirationDate: expirationDate!,
        })
        .then((response) => {
          if (response.success) {
            enqueueSnackbar(t("alert:success.externalAccessUpdated"), "success");
            onSuccess && onSuccess();
            closeModal();
          } else {
            enqueueSnackbar(t("alert:error.updatingExternalAccess", { message: response.message }), "error");
          }
        });
    }, [accessToUpdate, selectedPermissions, expirationDate]);
    // #endregion Funcs

    // #region Memos
    const selectedAssetIds = React.useMemo(() => selectedAssets.map((asset) => asset.id), [selectedAssets]);

    const customOptionsStatusText = React.useMemo(() => {
      let statusText = "";
      const nameIsEntered = virtualHeadName.length > 0;
      if (nameIsEntered) statusText += virtualHeadName;

      if (expirationDate) {
        if (nameIsEntered) statusText += " ・ ";
        const currentDate = new Date();
        const diffTime = expirationDate.getTime() - currentDate.getTime();
        const nbDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        statusText += t("component:body.expiresInDays", { count: nbDays });
      }

      if (showCustomEmail) {
        if (expirationDate !== null) statusText += " ・ ";
        statusText += t("component:body.customEmail");
      }

      return statusText;
    }, [selectedAssets, virtualHeadName, expirationDate, showCustomEmail]);

    const customOptionHelperText = React.useMemo(() => {
      let helperText = "Missing ";
      const nameIsEntered = virtualHeadName.length > 0;

      if (nameIsEntered) helperText += "external access name";

      if (!expirationDate) {
        if (nameIsEntered) helperText += " and ";
        helperText += "expiration date";
      }

      return helperText;
    }, [selectedAssets, virtualHeadName, expirationDate]);

    const footerMemo = React.useMemo(() => {
      if (creationStep === "no-asset") {
        return (
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
            <Button variant="contained" disabled={!selectedAssets.length} onClick={handleNextStep}>
              {t("common:next")}
            </Button>
          </Stack>
        );
      } else if (creationStep === "asset-selected") {
        const leftButton = canGoBack ? (
          <Button onClick={handlePreviousStep} disabled={creationLoading}>
            {t("button:previous")}
          </Button>
        ) : (
          <Button onClick={handleCancelClick} disabled={creationLoading}>
            {t("common:cancel")}
          </Button>
        );

        const creationButtonContent = creationLoading ? (
          <CircularProgress size={20} color="secondary" />
        ) : accessToUpdate ? (
          t("button:updateExternalAccess")
        ) : (
          t("button:createExternalAccesses", { count: selectedEmails.length })
        );
        return (
          <Stack direction="row" justifyContent="space-between">
            {leftButton}
            <Button
              variant="contained"
              onClick={accessToUpdate ? handleUpdateClick : handleCreateClick}
              disabled={!selectedEmails.length || !isFormValid || creationLoading || !selectedAssets.length}
            >
              {creationButtonContent}
            </Button>
          </Stack>
        );
      }
    }, [
      creationStep,
      selectedAssets,
      canGoBack,
      selectedEmails,
      isFormValid,
      creationLoading,
      accessToUpdate,
      handleUpdateClick,
      handleCreateClick,
    ]);
    // #endregion Memos

    // #region Effects
    React.useEffect(() => {
      if (!assets || !assets.length) {
        setCanGoBack(true);
        return;
      }
    }, [assets?.length]);

    React.useEffect(() => {
      userService.GetMailingList().then((mailingList) => {
        setFetchedMailingList(mailingList);
      });
    }, []);

    // #endregion Effects

    return (
      <Modal
        title={accessToUpdate ? t("modal:title.updateExternalAccess") : t("modal:title.createExternalAccess")}
        reference={ref}
        closeModal={handleCancelClick}
        width={600}
        footer={footerMemo}
      >
        <Slide
          key={creationStep}
          direction={direction}
          timeout={showTransition ? undefined : 0}
          in={true}
          mountOnEnter
          unmountOnExit
        >
          <Box>
            {creationStep === "no-asset" ? (
              <Stack spacing={2}>
                <OutlinedInput
                  fullWidth
                  placeholder={t("field:placeholder.searchAnAsset")}
                  size="large"
                  endAdornment={<SearchOutlinedIcon />}
                  value={searchValue}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                />
                <Stack spacing={1}>
                  {selectedAssets.map((asset, index) => (
                    <AssetAndChildrenCard
                      key={index}
                      asset={asset}
                      action="select"
                      selected={true}
                      onSelect={handleOnSelectAsset}
                    />
                  ))}
                  {searchLoading && (
                    <Box display="flex" justifyContent="center" width="100%">
                      <CircularProgress />
                    </Box>
                  )}
                  {searchResults
                    .filter((x) => !selectedAssetIds.includes(x.id))
                    .map((result) => (
                      <AssetAndChildrenCard
                        asset={result}
                        action="select"
                        selected={false}
                        onSelect={handleOnSelectAsset}
                      />
                    ))}
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <AssetAndChildrenAccordion
                  assets={selectedAssets}
                  nbToShow={3}
                  action={canGoBack ? "delete" : undefined}
                  onDelete={handleOnSelectAsset}
                />
                <PermissionsFormSection
                  selectedPermissions={selectedPermissions}
                  handlePermissionClick={handlePermissionClick}
                  variant="info"
                  allowedPermissions={GlobalConst.EXTERNAL_ACCESS_ALLOWED_PERMISSIONS}
                  disabledPermissions={GlobalConst.EXTERNAL_ACCESS_MANDATORY_PERMISSIONS}
                />
                <FormSection
                  title={t("common:email", { context: accessToUpdate ? "" : "plural" })}
                  expanded={emailsExpanded}
                  onExpand={!accessToUpdate ? toggleEmailsExpanded : undefined}
                  icon={<EmailOutlinedIcon />}
                  status={selectedEmails.length > 0 ? "custom" : "warning"}
                  statusText={t("common:configurationRequired")}
                  helperText={t("form:helper.atLeastOneEmailIsRequired")}
                  customStatus={
                    <Collapse in={!emailsExpanded}>
                      <AccessCustomChipsSummary labels={selectedEmails} nbToShow={2} chipsColor="info" />
                    </Collapse>
                  }
                >
                  <Stack spacing={2}>
                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                      {selectedEmails.map((email, index) => (
                        <AccessCustomChip
                          key={index}
                          label={email}
                          color="info"
                          onDelete={!accessToUpdate ? () => selectEmail(email) : undefined}
                        />
                      ))}
                    </Stack>
                    {!accessToUpdate && (
                      <>
                        <Stack spacing={1}>
                          <FormInputEmail
                            label={t("field:label.email")}
                            value={manualEmailValue}
                            onChange={(event) => setManualEmailValue(event.target.value)}
                            onKeyDown={handlePressEnterMailingManual}
                            validationProps={{
                              formId: formId,
                              formSectionId: "form-section-emails",
                              fieldId: "input-mail",
                              required: false,
                              initialState: true,
                            }}
                            stackProps={{
                              spacing: 1,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t("component:body.enterEmailAndEnter")}
                          </Typography>
                        </Stack>
                        <Autocomplete
                          popupIcon={<ExpandMoreRoundedIcon />}
                          options={fetchedMailingList.map((list) => list.name)}
                          value={null}
                          onChange={(event, value) => handleAutocompleteMailingListChange(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("field:label.loadReConfiguredMailingList")}
                              placeholder={t("field:placeholder.selectMailingList")}
                            />
                          )}
                        />
                      </>
                    )}
                  </Stack>
                </FormSection>

                <FormSection
                  title={t("common:option", { context: accessToUpdate ? "" : "plural" })}
                  expanded={optionsExpanded}
                  onExpand={toggleOptionsExpanded}
                  icon={<SettingsOutlinedIcon />}
                  status={
                    !expirationDate || (selectedAssets.length > 1 && !virtualHeadName.length) ? "warning" : "custom"
                  }
                  statusText={t("common:configurationRequired")}
                  helperText={customOptionHelperText}
                  customStatus={
                    <Collapse in={!optionsExpanded}>
                      <Typography variant="body2">{customOptionsStatusText}</Typography>
                    </Collapse>
                  }
                >
                  <Stack spacing={2}>
                    {!accessToUpdate && (
                      <Stack spacing={1}>
                        <FormInputText
                          label={t("field:label.accessName")}
                          validationProps={{
                            formId: formId,
                            formSectionId: "form-section-options",
                            fieldId: "virtual-head-name",
                            required: true,
                            initialState: false,
                          }}
                          value={virtualHeadName}
                          onChange={(e) => setVirtualHeadName(e.target.value)}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t("component:body.assetsWillBeGroupedUnderSameFolder")}
                        </Typography>
                      </Stack>
                    )}
                    <Stack spacing={1}>
                      <FormInputDate
                        label={t("field:label.expirationDate")}
                        value={dayjs(expirationDate)}
                        onChange={(newValue) => setExpirationDate(newValue ? dayjs(newValue).toDate() : null)}
                        hideReset
                        validationProps={{
                          formId: formId,
                          formSectionId: "form-section-options",
                          fieldId: "expire-date",
                          required: true,
                          initialState: false,
                          onlyInFuture: true,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t("component:body.externalUsersExpirationAccess")}
                      </Typography>
                    </Stack>
                    {!accessToUpdate && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch checked={showCustomEmail} onChange={(e) => setShowCustomEmail(e.target.checked)} />
                          }
                          label={t("field:label.sendCustomEmailToExternalUsers")}
                        />
                        <Collapse in={showCustomEmail}>
                          <Stack spacing={2}>
                            <TextField
                              label={t("field:label.emailObject")}
                              placeholder={t("field:placeholder.filesAccessGranted")}
                              value={emailObject}
                              onChange={(e) => setEmailObject(e.target.value)}
                              fullWidth
                              variant="outlined"
                            />
                            <TextField
                              label={t("field:label.emailContent")}
                              placeholder={t("field:placeholder.accessLinkInvitation")}
                              value={emailContent}
                              onChange={(e) => setEmailContent(e.target.value)}
                              fullWidth
                              multiline
                              rows={4}
                              variant="outlined"
                            />
                          </Stack>
                        </Collapse>
                      </>
                    )}
                  </Stack>
                </FormSection>
              </Stack>
            )}
          </Box>
        </Slide>
      </Modal>
    );
  }
);

export default ModalCreateOrUpdateExternalAccess;
